import React from "react";
import { Link, useLocation } from "react-router-dom";
import DifficultyStatsBar from "./DifficultyStatsBar";
import { 
  Menu, 
  Code2, 
  Building2, 
  MessageSquare, 
  BookOpen,
  ChevronLeft,
  Home,
  Settings,
  HelpCircle
} from "lucide-react";

interface LeftNavPaneProps {
  difficultyStats: {
    easy: number;
    medium: number;
    hard: number;
  };
  isCollapsed: boolean;
  setIsCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
}

const navItems = [
  { path: "/algorithms", label: "Algorithms", icon: Code2 },
  { path: "/system-design", label: "System Design", icon: Building2 },
  { path: "/behavioral", label: "Behavioral", icon: MessageSquare },
  { path: "/blog", label: "Blog", icon: BookOpen },
];

const LeftNavPane: React.FC<LeftNavPaneProps> = ({
  difficultyStats,
  isCollapsed,
  setIsCollapsed,
}) => {
  const location = useLocation();

  return (
    <div
      className={`bg-gray-900 border-r border-gray-800 h-screen flex flex-col transition-all duration-300 ${
        isCollapsed ? "w-20" : "w-64"
      }`}
    >
      {/* Header */}
      <div className="p-4 border-b border-gray-800">
        <div className="flex items-center justify-between">
          {!isCollapsed && (
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gradient-to-br from-emerald-400 to-cyan-500 rounded-lg flex items-center justify-center">
                <Code2 className="w-5 h-5 text-white" />
              </div>
              <span className="text-lg font-bold bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">
                AlgoBuddy
              </span>
            </div>
          )}
          {isCollapsed && (
            <div className="w-8 h-8 bg-gradient-to-br from-emerald-400 to-cyan-500 rounded-lg flex items-center justify-center mx-auto">
              <Code2 className="w-5 h-5 text-white" />
            </div>
          )}
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="p-2 rounded-lg hover:bg-gray-800 text-gray-400 hover:text-white transition-colors"
          >
            {isCollapsed ? <Menu className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 overflow-y-auto">
        <ul className="space-y-2">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + "/");
            const Icon = item.icon;
            
            return (
              <li key={item.path}>
                <Link
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                    isActive
                      ? "bg-gradient-to-r from-emerald-500/20 to-cyan-500/20 text-emerald-400 border border-emerald-500/30"
                      : "text-gray-400 hover:bg-gray-800 hover:text-white border border-transparent"
                  }`}
                >
                  <Icon className={`w-5 h-5 flex-shrink-0 ${isActive ? "text-emerald-400" : ""}`} />
                  {!isCollapsed && (
                    <span className="font-medium">{item.label}</span>
                  )}
                </Link>
              </li>
            );
          })}
        </ul>

        {/* Stats Section */}
        {!isCollapsed && (
          <div className="mt-8">
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3 px-4">
              Your Progress
            </div>
            <DifficultyStatsBar stats={difficultyStats} />
          </div>
        )}
      </nav>

      {/* Footer */}
      <div className="p-4 border-t border-gray-800">
        {!isCollapsed ? (
          <div className="flex items-center gap-2">
            <button className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-gray-400 hover:bg-gray-800 hover:text-white transition-colors">
              <Settings className="w-4 h-4" />
              <span className="text-sm">Settings</span>
            </button>
            <button className="p-2 rounded-lg text-gray-400 hover:bg-gray-800 hover:text-white transition-colors">
              <HelpCircle className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <button className="p-2 rounded-lg text-gray-400 hover:bg-gray-800 hover:text-white transition-colors">
              <Settings className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default LeftNavPane;